"use client"
import React from "react";
import { Match as IMatch } from "@/sources/kicker-amateur/types";
import { MatchTeam } from "./MatchTeam";
import { MatchScore } from "./MatchScore";
import Link from "next/link";
import { useTeam } from "@/app/pwa/lib/pwa-context";
import { pwaTeamGamePath } from "@/utils/urlHelper";
import { formatDateStyleFull, getKickoffDetails } from "@/utils/datetimeUtils";



export function MatchHeader({
  match,
  className
} : {
  match: IMatch,
  className?: string
}) {
  const { kickoffDateString } = getKickoffDetails(match);
  return <div className={`text-center leading-tight ${className || ""}`}>
  <h4 className="font-bold">
    {formatDateStyleFull(kickoffDateString)}
  </h4>
  <h5 className="font-normal text-gray-200 font-condensed">
    {match.Contest?.Name}
    {match.Round && match.Contest?.ContestType != "FriendlyMatch" && (
      <>
        , <span className="@sm/card:hidden">{match.Round.ShortName}</span>
        <span className="hidden @sm/card:inline">{match.Round.Name}</span>
      </>
    )}
  </h5>
</div>
}

interface MatchProps {
  teamId: number;
  match: IMatch;
  showBigIcons?: boolean;
  linkToMatch?: boolean;
  linkToTeam?: boolean;
  className?: string;
  forcedATeamName?: boolean
  forcedBTeamName?: boolean
  size?: "base" | "lg",
  showHalfTimeScore?: boolean
  widget?: boolean
}

export function Match({
  match,
  teamId,
  showBigIcons = true,
  linkToMatch = true,
  linkToTeam = true,
  className,
  forcedATeamName,
  forcedBTeamName,
  size = "base",
  showHalfTimeScore = true,
  widget = false
}: MatchProps) {
  const { ATeam, BTeam, Contest } = match;
  const ATeamData = useTeam(ATeam?.Id!)
  const BTeamData = useTeam(ATeam?.Id!)
  let correctTeamId = teamId
  if(!correctTeamId && ATeamData){
    correctTeamId = ATeamData.Id!
  }
  if(!correctTeamId && BTeamData){
    correctTeamId = BTeamData.Id!
  }

  if (!(ATeam && BTeam)) {
    return <span>Unable to load match</span>;
  }

  const MatchComponent = (
    <div className={`flex gap-3 @container/match  group/match size-${size}`}>
      <MatchTeam
        team={ATeam}
        teamId={correctTeamId}
        contest={Contest!}
        position={"left"}
        showBigIcons={!!showBigIcons}
        linkToTeam={linkToTeam && !linkToMatch}
        teamTypeName={forcedATeamName}
        size={size}
        widget={widget}
      />
      <MatchScore match={match} showHalfTimeScore={showHalfTimeScore} />
      <MatchTeam
        team={BTeam}
        teamId={correctTeamId}
        contest={Contest!}
        position={"right"}
        showBigIcons={!!showBigIcons}
        linkToTeam={linkToTeam && !linkToMatch}
        teamTypeName={forcedBTeamName}
        size={size}
        widget={widget}
      />
    </div>
  );

  function matchComponentWrapper() {
    if (widget) {
      return <div className={className}>
        {MatchComponent}
      </div>
    } else if (linkToMatch) {
      return <Link
        className={`${className || ""} block interactive-cell`}
        href={pwaTeamGamePath(`${correctTeamId}`, match.Id!.toString(), {tab:"organisation"})}>
        {MatchComponent}
      </Link>
    } else {
      return <div className={className}>
        {MatchComponent}
      </div>
    }
  }

  return matchComponentWrapper();
}
